import React, { useState, useEffect, useContext } from 'react';
import {
  Car, Activity, DollarSign, Calendar, TrendingUp,
  MapPin, Settings, PieChart, HelpCircle, Search,
  Loader2, Fuel, ArrowRight, Info, CheckCircle2, FileText
} from 'lucide-react';

import { motion } from "framer-motion";
import { AppContext } from '../context/AppContext';
// --- CONSTANTS & CONFIGURATION ---


const CONSTANTS = {
  BENCHMARKS: {
    class8: { label: 'Class 8 Truck', avgCpm: 2.26, maintCap: 0.20, type: 'commercial' },
    bus: { label: 'School Bus', avgCpm: 1.45, maintCap: 0.18, type: 'commercial' },
    service: { label: 'Service Van', avgCpm: 0.85, maintCap: 0.12, type: 'commercial' },
    pickup: { label: 'Pickup (Half-Ton/Mid)', avgCpm: 0.99, maintCap: 0.14, type: 'commercial' },
    suv: { label: 'SUV (Compact/Med)', avgCpm: 0.84, maintCap: 0.12, type: 'consumer' },
    sedan: { label: 'Corp Sedan', avgCpm: 0.66, maintCap: 0.11, type: 'consumer' },
    motorcycle: { label: 'Motorcycle / Scooter', avgCpm: 0.25, maintCap: 0.05, type: 'consumer' },
    rickshaw: { label: 'Auto-Rickshaw (3W)', avgCpm: 0.15, maintCap: 0.04, type: 'commercial' }
  },
  REGIONS: {
    US: { currency: '$', unit: 'mi', label: 'United States', rate: 1.0 },
    CA: { currency: 'CAD', unit: 'km', label: 'Canada', rate: 1.41 },
    AU: { currency: 'AUD', unit: 'km', label: 'Australia', rate: 1.58 },
    IN: { currency: '₹', unit: 'km', label: 'India', rate: 86.5 },
    AE: { currency: 'AED', unit: 'km', label: 'UAE', rate: 3.67 },
    SA: { currency: 'SAR', unit: 'km', label: 'Saudi Arabia', rate: 3.75 },
    QA: { currency: 'QAR', unit: 'km', label: 'Qatar', rate: 3.64 },
    KW: { currency: 'KWD', unit: 'km', label: 'Kuwait', rate: 0.31 },
    OM: { currency: 'OMR', unit: 'km', label: 'Oman', rate: 0.38 },
    BH: { currency: 'BHD', unit: 'km', label: 'Bahrain', rate: 0.37 }
  },
  TOOLTIPS: {
    vehicleType: "Select the category that best fits your vehicle to apply the correct industry cost benchmarks.",
    fuelSource: "Primary energy source. Selecting CNG changes measurement units to kg.",
    regNumber: "Vehicle License Plate or Asset ID for identification.",
    modelYear: "Manufacturing year. Helps determine expected depreciation and maintenance curves.",
    purchasePrice: "Total cost to acquire the vehicle, including taxes, dealer fees, and upfitting expenses.",
    serviceLife: "Total expected mileage/distance before the vehicle is sold or scrapped. Used to calculate depreciation per unit distance.",
    resaleValue: "Estimated residual value at end of life. A higher resale value reduces your depreciation cost.",
    method: "How the asset is funded. 'Loan' tracks interest; 'Lease' tracks monthly rental fees. Cash assumes no monthly interest cost.",
    apr: "Annual Percentage Rate. Used to estimate the interest portion of your monthly payment.",
    monthlyPayment: "Total monthly cash outflow (Principal + Interest). We allocate this based on days in the reporting period.",
    insurance: "Total annual premium. Will be prorated for the specific dates selected in the next step.",
    regFees: "Annual cost for plates, registration, and inspection stickers.",
    hut: "Heavy Use Tax. Specific to commercial vehicles > 55,000 lbs.",
    admin: "Estimated annual soft costs (software subscriptions, fleet manager salary) allocated to this specific unit.",
    distance: "The actual distance traveled during the reporting period. This is the denominator for the Cost Per Mile/Km calculation.",
    fuelSpend: "Total amount spent on fuel during this period. Include all fuel card transactions.",
    quantity: "Total volume or mass of fuel consumed. Required to calculate efficiency.",
    routine: "Scheduled maintenance (Oil changes, PMs, Inspections). Consistent spending here is good.",
    corrective: "Unplanned repairs and breakdowns. High values here indicate risk and poor vehicle health.",
    tires: "Cost for tire replacement, rotation, or repair during this period.",
    wash: "Cleaning and detailing costs.",
    wages: "Total driver pay allocable to this period and vehicle.",
    benefits: "Associated labor burden (Payroll taxes, health insurance, 401k).",
    tolls: "Road tolls, parking fees, and weigh station costs.",
    fines: "Citations, compliance fines, or overweight penalties."
  }
};


// --- PURE LOGIC HELPERS ---


const safeFloat = (val) => {
  const num = parseFloat(val);
  return isNaN(num) ? 0 : num;
};


const getTableDepreciationRate = (ageMonths) => {
  if (ageMonths <= 6) return 0;
  if (ageMonths <= 12) return 0.05;
  if (ageMonths <= 36) return 0.05;
  if (ageMonths <= 60) return 0.10;
  if (ageMonths <= 120) return 0.01;
  return 0.02;
};


const calculateTCO = (config, fixed, variable, telematics, periodDays) => {
  const miles = safeFloat(telematics.periodDistance);
 
  if (miles <= 0) return null;


  const region = CONSTANTS.REGIONS[config.region];
  const purch = safeFloat(fixed.purchasePrice);
 
  // 1. Depreciation Logic
  let periodDepr = 0;
  let deprMethod = "";
 
  if (fixed.serviceLifeMiles && fixed.resaleValue) {
    const resale = safeFloat(fixed.resaleValue);
    const lifeMiles = safeFloat(fixed.serviceLifeMiles) || 1;
    const deprPerMile = (purch - resale) / lifeMiles;
    periodDepr = deprPerMile * miles;
    deprMethod = "Allocated (Mileage Basis)";
  } else {
    const modelYear = safeFloat(config.modelYear) || new Date().getFullYear();
    const acquireDate = new Date(`${modelYear}-01-01`);
    const today = new Date();
    const ageMonths = (today.getFullYear() - acquireDate.getFullYear()) * 12 + (today.getMonth() - acquireDate.getMonth());
    const annualRate = getTableDepreciationRate(ageMonths);
    periodDepr = purch * annualRate * (periodDays / 365);
    deprMethod = `Allocated (Age Table: ${(annualRate * 100).toFixed(0)}% Rate)`;
  }


  // 2. Fixed Costs
  const annualFixed =
    safeFloat(fixed.insuranceAnnual) +
    safeFloat(fixed.registrationAnnual) +
    safeFloat(fixed.hutAnnual) +
    safeFloat(fixed.permitsAnnual) +
    safeFloat(fixed.adminOverheadAnnual);
 
  const dailyFixed = annualFixed / 365;
  const periodFixedOverhead = dailyFixed * periodDays;
 
  const monthlyPayment = fixed.financeMethod === 'cash' ? 0 : safeFloat(fixed.monthlyPayment);
  const periodFinance = ((monthlyPayment * 12) / 365) * periodDays;
  const totalFixed = periodDepr + periodFixedOverhead + periodFinance;


  // 3. Variable Costs
  const fuel = safeFloat(variable.fuelSpend);
  const routine = safeFloat(variable.routineMaint);
  const corrective = safeFloat(variable.correctiveMaint);
  const tires = safeFloat(variable.tires);
  const washing = safeFloat(variable.washing);
  const labor =
    safeFloat(variable.driverWages) +
    safeFloat(variable.benefits) +
    safeFloat(variable.tolls) +
    safeFloat(variable.fines);


  const totalMaint = routine + corrective + tires + washing;
  const totalVariable = fuel + totalMaint + labor;
  const totalCost = totalFixed + totalVariable;


  // 4. Analysis
  const cpm = totalCost / miles;
 
  // Tax Logic
  let taxDeduction = 0;
  if (config.region === 'CA') {
    if (miles <= 5000) taxDeduction = miles * 0.72;
    else taxDeduction = (5000 * 0.72) + ((miles - 5000) * 0.66);
  } else if (config.region === 'AU') {
    taxDeduction = Math.min(miles, 5000) * 0.88;
  }


  // Benchmarking
  const bench = CONSTANTS.BENCHMARKS[config.vehicleType];
  const unitDivisor = region.unit === 'km' ? 1.60934 : 1;
  const localBenchmarkCpm = (bench.avgCpm * region.rate) / unitDivisor;
 
  return {
    cpm: cpm.toFixed(2),
    localBenchmarkCpm: localBenchmarkCpm.toFixed(2),
    periodMiles: miles,
    breakdown: {
      depreciation: (periodDepr / miles).toFixed(2),
      deprMethod,
      finance: (periodFinance / miles).toFixed(2),
      insurance: (periodFixedOverhead / miles).toFixed(2),
      fuel: (fuel / miles).toFixed(2),
      maintenance: (totalMaint / miles).toFixed(2),
      labor: (labor / miles).toFixed(2)
    },
    totals: {
      fixed: totalFixed.toFixed(2),
      variable: totalVariable.toFixed(2),
      grand: totalCost.toFixed(2)
    },
    taxDeduction: taxDeduction.toFixed(2),
    regionCurrency: region.currency,
    regionUnit: region.unit,
    alerts: [
      cpm > localBenchmarkCpm ? { type: 'warning', msg: 'Cost per unit exceeds regional benchmark' } : null,
      corrective > routine ? { type: 'danger', msg: 'Corrective spend exceeds routine maintenance' } : null
    ].filter(Boolean)
  };
};


// --- SUB-COMPONENTS ---


const LabelWithTooltip = ({ label, tooltip, color }) => {
  const [showTooltip, setShowTooltip] = useState(false);



  return (
    <div
      className="d-flex align-items-center mb-2 position-relative"
      style={{ width: 'fit-content' }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <label className={`form-label mb-0 me-1 small fw-bold ${color || 'text-secondary'}`} style={{cursor: 'help'}}>
        {label}
      </label>
      {tooltip && <HelpCircle size={14} className="text-muted" style={{cursor: 'help'}} />}
     
      {/* Bootstrap styled custom tooltip */}
      {showTooltip && tooltip && (
        <div
          className="position-absolute bg-dark text-white p-2 rounded shadow-sm"
          style={{
            bottom: '100%',
            left: 0,
            width: '220px',
            fontSize: '0.75rem',
            zIndex: 1050,
            pointerEvents: 'none'
          }}
        >
          {tooltip}
          {/* Arrow */}
          <div
            className="position-absolute"
            style={{
              top: '100%',
              left: '10px',
              borderLeft: '5px solid transparent',
              borderRight: '5px solid transparent',
              borderTop: '5px solid #212529'
            }}
          />
        </div>
      )}
    </div>
  );
};


const ProfileStep = ({ config, setConfig, onNext }) => {
  const [isFetching, setIsFetching] = useState(false);


  const fetchDetails = () => {
    setIsFetching(true);
    setTimeout(() => {
      // Mock Data Fetch
      const isIndia = config.regNumber.toUpperCase().startsWith('MH') || config.regNumber.toUpperCase().startsWith('DL');
      setConfig(prev => ({
        ...prev,
        makeModel: isIndia ? "Tata Ace Gold" : "Ford F-150",
        modelYear: "2023",
        vehicleType: isIndia ? "service" : "pickup",
        fuelSource: isIndia ? "cng" : "petrol",
        region: isIndia ? "IN" : "US"
      }));
      setIsFetching(false);
    }, 1000);
  };


  return (
    <div className="card shadow-sm border-0">
      <div className="card-header bg-white border-bottom py-3">
        <h5 className="card-title mb-0 fw-bold text-dark">1. Vehicle Profile Setup</h5>
      </div>
      <div className="card-body p-4">
       
        {/* Registration Lookup */}
        <div className="p-4 mb-4  border rounded-3">
          <h6 className="text-black text-uppercase fw-bold mb-3 d-flex align-items-center gap-2">
            <Search size={16} /> Start by Identifier
          </h6>
          <div className="row g-3 align-items-end">
            <div className="col-md-8">
              <LabelWithTooltip label="Registration / License Plate" tooltip={CONSTANTS.TOOLTIPS.regNumber} />
              <input
                type="text"
                className="form-control form-control-lg text-uppercase font-monospace"
                placeholder="e.g. MH-01-AB-1234"
                value={config.regNumber}
                onChange={e => setConfig({...config, regNumber: e.target.value})}
              />
            </div>
            <div className="col-md-4">
              <button
                onClick={fetchDetails}
                disabled={!config.regNumber || isFetching}
                className="btn  btn-lg w-100 d-flex justify-content-center align-items-center"
              >
                {isFetching ? <Loader2 size={20} className="animate-spin" /> : 'Fetch Details'}
              </button>
            </div>
          </div>
        </div>


        <div className="row g-4">
          {/* Vehicle Specs */}
          <div className="col-md-6">
            <h6 className="text-secondary text-uppercase fw-bold mb-3">Vehicle Specs</h6>
            <div className="mb-3">
              <label className="form-label small fw-bold text-secondary">Make & Model</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. Tata Ace"
                value={config.makeModel}
                onChange={e => setConfig({...config, makeModel: e.target.value})}
              />
            </div>
            <div className="row g-3">
              <div className="col-6">
                <LabelWithTooltip label="Mfg Year" tooltip={CONSTANTS.TOOLTIPS.modelYear} />
                <input
                  type="number"
                  className="form-control"
                  placeholder="YYYY"
                  value={config.modelYear}
                  onChange={e => setConfig({...config, modelYear: e.target.value})}
                />
              </div>
              <div className="col-6">
                <LabelWithTooltip label="Fuel Source" tooltip={CONSTANTS.TOOLTIPS.fuelSource} />
                <select
                  className="form-select"
                  value={config.fuelSource}
                  onChange={e => setConfig({...config, fuelSource: e.target.value})}
                >
                  <option value="petrol">Petrol</option>
                  <option value="diesel">Diesel</option>
                  <option value="cng">CNG</option>
                </select>
              </div>
            </div>
          </div>


          {/* Classification & Location */}
          <div className="col-md-6">
            <h6 className="text-secondary text-uppercase fw-bold mb-3">Classification & Location</h6>
            <div className="mb-3">
              <LabelWithTooltip label="Vehicle Type" tooltip={CONSTANTS.TOOLTIPS.vehicleType} />
              <select
                className="form-select"
                value={config.vehicleType}
                onChange={e => setConfig({...config, vehicleType: e.target.value})}
              >
                {Object.entries(CONSTANTS.BENCHMARKS).map(([key, data]) => (
                  <option key={key} value={key}>{data.label}</option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label small fw-bold text-secondary">Region (Currency/Units)</label>
              <select
                className="form-select"
                value={config.region}
                onChange={e => setConfig({...config, region: e.target.value})}
              >
                {Object.entries(CONSTANTS.REGIONS).map(([code, data]) => (
                  <option key={code} value={code}>{data.label} ({data.currency})</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
      <div className="card-footer bg-white border-top p-3 text-end">
        <button onClick={onNext} className="btn d-inline-flex align-items-center gap-2">
          Confirm & Continue <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};


const FixedCostsStep = ({ data, setData, config, onNext, onBack, loadDemo }) => {
  const currency = CONSTANTS.REGIONS[config.region].currency;
  const unit = CONSTANTS.REGIONS[config.region].unit;


  return (
    <div className="card shadow-sm border-0">
      <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0 fw-bold text-dark">2. Asset Acquisition & Fixed Costs</h5>
        <button onClick={loadDemo} className="btn btn-link btn-sm text-decoration-none p-0">Autofill Demo Data</button>
      </div>
      <div className="card-body p-4">
       
        {/* Acquisition */}
        <div className="row mb-4">
          <div className="col-12">
            <h6 className="text-secondary text-uppercase fw-bold mb-3">Acquisition & Depreciation</h6>
            <div className="alert alert-info d-flex align-items-center py-2 px-3 mb-3 small">
              <Info size={16} className="me-2 flex-shrink-0" />
              <span>If <strong>Svc Life</strong> or <strong>Resale Value</strong> are empty, we use an Age-Based Table (Mfg Year: {config.modelYear}).</span>
            </div>
          </div>
         
          <div className="col-md-6 mb-3">
            <LabelWithTooltip label="Purchase Price" tooltip={CONSTANTS.TOOLTIPS.purchasePrice} />
            <div className="input-group">
              <span className="input-group-text">{currency}</span>
              <input
                type="number"
                className="form-control"
                placeholder="0.00"
                value={data.purchasePrice}
                onChange={e => setData({...data, purchasePrice: e.target.value})}
              />
            </div>
          </div>
         
          <div className="col-md-3 mb-3">
            <LabelWithTooltip label={`Svc Life (${unit})`} tooltip={CONSTANTS.TOOLTIPS.serviceLife} />
            <input
              type="number"
              className="form-control"
              placeholder="(Optional)"
              value={data.serviceLifeMiles}
              onChange={e => setData({...data, serviceLifeMiles: e.target.value})}
            />
          </div>
         
          <div className="col-md-3 mb-3">
            <LabelWithTooltip label="Resale Value" tooltip={CONSTANTS.TOOLTIPS.resaleValue} />
            <div className="input-group">
              <span className="input-group-text">{currency}</span>
              <input
                type="number"
                className="form-control"
                placeholder="(Optional)"
                value={data.resaleValue}
                onChange={e => setData({...data, resaleValue: e.target.value})}
              />
            </div>
          </div>
        </div>


        {/* Financing */}
        <div className="row mb-4">
          <div className="col-12">
            <h6 className="text-secondary text-uppercase fw-bold mb-3">Financing (Capital)</h6>
          </div>
         
          <div className={data.financeMethod === 'cash' ? 'col-md-12 mb-3' : 'col-md-4 mb-3'}>
            <LabelWithTooltip label="Method" tooltip={CONSTANTS.TOOLTIPS.method} />
            <select
              className="form-select"
              value={data.financeMethod}
              onChange={e => setData({...data, financeMethod: e.target.value})}
            >
              <option value="loan">Loan</option>
              <option value="lease">Lease</option>
              <option value="cash">Cash</option>
            </select>
          </div>


          {data.financeMethod !== 'cash' && (
            <>
              <div className="col-md-4 mb-3">
                <LabelWithTooltip label="Interest APR %" tooltip={CONSTANTS.TOOLTIPS.apr} />
                <input
                  type="number"
                  className="form-control"
                  placeholder="7.5"
                  value={data.interestRate}
                  onChange={e => setData({...data, interestRate: e.target.value})}
                />
              </div>
              <div className="col-md-4 mb-3">
                <LabelWithTooltip label="Monthly Payment" tooltip={CONSTANTS.TOOLTIPS.monthlyPayment} />
                <div className="input-group">
                  <span className="input-group-text">{currency}</span>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="0.00"
                    value={data.monthlyPayment}
                    onChange={e => setData({...data, monthlyPayment: e.target.value})}
                  />
                </div>
              </div>
            </>
          )}
        </div>


        {/* Annual Compliance */}
        <div className="row">
          <div className="col-12">
            <h6 className="text-secondary text-uppercase fw-bold mb-3">Annual Compliance & Overhead</h6>
          </div>
          {[
            { label: 'Insurance (Yr)', key: 'insuranceAnnual', tip: 'insurance' },
            { label: 'Reg/License', key: 'registrationAnnual', tip: 'regFees' },
            ...(CONSTANTS.BENCHMARKS[config.vehicleType].type === 'commercial'
                ? [{ label: 'Heavy Use Tax', key: 'hutAnnual', tip: 'hut' }]
                : []),
            { label: 'Admin Overhead', key: 'adminOverheadAnnual', tip: 'admin' }
          ].map((item, idx) => (
            <div className="col-md-3 mb-3" key={idx}>
              <LabelWithTooltip label={item.label} tooltip={CONSTANTS.TOOLTIPS[item.tip]} />
              <div className="input-group">
                <span className="input-group-text">{currency}</span>
                <input
                  type="number"
                  className="form-control"
                  placeholder="0.00"
                  value={data[item.key]}
                  onChange={e => setData({...data, [item.key]: e.target.value})}
                />
              </div>
            </div>
          ))}
        </div>


      </div>
      <div className="card-footer bg-white border-top p-3 d-flex justify-content-between">
        <button onClick={onBack} className="btn btn-outline-secondary">Back</button>
        <button onClick={onNext} className="btn d-inline-flex align-items-center gap-2">
          Next: Variable Costs <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};


 const VariableCostsStep = ({ data, setData, telematics, setTelematics, config, reportPeriod, onCalc, onBack }) => {
  const currency = CONSTANTS.REGIONS[config.region].currency;
  const unit = CONSTANTS.REGIONS[config.region].unit;
  const fuelUnit = config.fuelSource === 'cng' ? 'Mass (kg)' : (unit === 'mi' ? 'Gallons' : 'Liters');


  return (
    <div className="card shadow-sm border-0">
      <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0 fw-bold text-dark">3. Operational Expense Log</h5>
        <span className="badge  text-dark fw-normal border">{reportPeriod.start} to {reportPeriod.end}</span>
      </div>
     
      {/* Telematics Input */}
      <div className=" border-bottom p-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="bg-black bg-opacity-10 p-2 rounded-circle text-black">
              <Activity size={24} />
            </div>
            <div>
              <div className="fw-bold text-black text-uppercase small">Telematics Distance (Required)</div>
              <div className="text-muted small">Total {unit === 'mi' ? 'miles' : 'kilometers'} traveled in this period</div>
            </div>
          </div>
          <div className="input-group" style={{maxWidth: '200px'}}>
            <input
              type="number"
              className="form-control"
              placeholder="e.g. 10000"
              value={telematics.periodDistance}
              onChange={e => setTelematics({...telematics, periodDistance: e.target.value})}
            />
            <span className="input-group-text">{unit}</span>
          </div>
        </div>
      </div>


      <div className="card-body p-4">
        <div className="row g-4">
          {/* Fuel */}
          <div className="col-md-6">
            <h6 className="text-secondary text-uppercase fw-bold mb-3 d-flex align-items-center gap-2">
              <Fuel size={16} /> {config.fuelSource.toUpperCase()} CONSUMPTION
            </h6>
            <div className="row g-3">
              <div className="col-12">
                <LabelWithTooltip label="Total Spend" tooltip={CONSTANTS.TOOLTIPS.fuelSpend} />
                <div className="input-group">
                  <span className="input-group-text">{currency}</span>
                  <input
                    type="number"
                    className="form-control"
                    value={data.fuelSpend}
                    onChange={e => setData({...data, fuelSpend: e.target.value})}
                  />
                </div>
              </div>
              <div className="col-12">
                <LabelWithTooltip label={fuelUnit} tooltip={CONSTANTS.TOOLTIPS.quantity} />
                <input
                  type="number"
                  className="form-control"
                  placeholder="Qty"
                  value={data.fuelQuantity}
                  onChange={e => setData({...data, fuelQuantity: e.target.value})}
                />
              </div>
            </div>
          </div>


          {/* Maintenance */}
          <div className="col-md-6">
            <h6 className="text-secondary text-uppercase fw-bold mb-3 d-flex align-items-center gap-2">
              <Settings size={16} /> Maintenance & Repair
            </h6>
            <div className="row g-3">
              {[
                { key: 'routineMaint', label: 'Routine (PM)', tip: 'routine', color: 'text-success' },
                { key: 'correctiveMaint', label: 'Corrective', tip: 'corrective', color: 'text-danger' },
                { key: 'tires', label: 'Tires', tip: 'tires' },
                { key: 'washing', label: 'Wash/Detail', tip: 'wash' }
              ].map((field) => (
                <div className="col-6" key={field.key}>
                  <LabelWithTooltip label={field.label} tooltip={CONSTANTS.TOOLTIPS[field.tip]} color={field.color} />
                  <div className="input-group">
                    <span className="input-group-text">{currency}</span>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="0.00"
                      value={data[field.key]}
                      onChange={e => setData({...data, [field.key]: e.target.value})}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>


          {/* Labor */}
          {CONSTANTS.BENCHMARKS[config.vehicleType].type === 'commercial' && (
            <div className="col-12 border-top pt-4">
              <h6 className="text-secondary text-uppercase fw-bold mb-3">3. Labor & Misc</h6>
              <div className="row g-3">
                {['driverWages', 'benefits', 'tolls', 'fines'].map((field) => (
                  <div className="col-md-3" key={field}>
                    <LabelWithTooltip
                      label={field.replace(/([A-Z])/g, ' $1').trim().replace(/^\w/, c => c.toUpperCase())}
                      tooltip={CONSTANTS.TOOLTIPS[field === 'driverWages' ? 'wages' : field]}
                    />
                    <div className="input-group">
                      <span className="input-group-text">{currency}</span>
                      <input
                        type="number"
                        className="form-control"
                        value={data[field]}
                        onChange={e => setData({...data, [field]: e.target.value})}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>


      <div className="card-footer bg-white border-top p-3 d-flex justify-content-between">
        <button onClick={onBack} className="btn btn-outline-secondary">Back</button>
        <button onClick={onCalc} className="btn btn-success d-inline-flex align-items-center gap-2">
          <FileText size={16} /> Calculate Report
        </button>
      </div>
    </div>
  );
};


const DashboardStep = ({ results, config, onEdit }) => {
  if (!results) return null;
  const currency = results.regionCurrency;
  const unit = results.regionUnit;


  return (
    <div className="container-fluid p-0">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="fw-bold text-dark mb-0">Analysis: {CONSTANTS.BENCHMARKS[config.vehicleType].label}</h4>
        <div className="btn-group">
          <button className="btn btn-outline-secondary">Export PDF</button>
          <button onClick={onEdit} className="btn btn-outline-secondary">Edit Data</button>
        </div>
      </div>


      <div className="row g-4 mb-4">
        {/* KPI Card */}
        <div className="col-md-4">
          <div className="card h-100 shadow-sm border-top border-4 border-black" style={{color:"rgb(156, 73, 0);"}}>
            <div className="card-body text-center py-4">
              <h6 className="text-uppercase text-muted fw-bold mb-3">Total Cost Per {unit}</h6>
              <h2 className="display-4 fw-bold text-dark mb-0">
                {currency}{results.cpm}
                <span className="fs-6 text-muted ms-2">/ {unit}</span>
              </h2>
              <div className="mt-4 pt-3 border-top d-flex justify-content-between text-muted small">
                <span>Industry Benchmark:</span>
                <span className="fw-bold text-dark">{currency}{results.localBenchmarkCpm}</span>
              </div>
            </div>
          </div>
        </div>


        {/* Composition Card */}
        <div className="col-md-4">
          <div className="card h-100 shadow-sm">
            <div className="card-body">
              <h6 className="text-uppercase text-muted fw-bold mb-4">Cost Composition</h6>
             
              <div className="mb-4">
                <div className="d-flex justify-content-between mb-1 small">
                  <span><span className="d-inline-block rounded-circle bg-primary me-2" style={{width: 8, height: 8}}></span>Fixed (Allocated)</span>
                  <span className="fw-bold">{currency}{results.fixedCpm}</span>
                </div>
                <div className="progress" style={{height: 8}}>
                  <div className="progress-bar bg-primary" role="progressbar" style={{width: `${(parseFloat(results.fixedCpm) / parseFloat(results.cpm)) * 100}%`}}></div>
                </div>
              </div>


              <div>
                <div className="d-flex justify-content-between mb-1 small">
                  <span><span className="d-inline-block rounded-circle bg-warning me-2" style={{width: 8, height: 8}}></span>Variable (Ops)</span>
                  <span className="fw-bold">{currency}{results.varCpm}</span>
                </div>
                <div className="progress" style={{height: 8}}>
                  <div className="progress-bar bg-warning" role="progressbar" style={{width: `${(parseFloat(results.varCpm) / parseFloat(results.cpm)) * 100}%`}}></div>
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Alerts Card */}
        <div className="col-md-4">
          <div className="card h-100 shadow-sm">
            <div className="card-body">
              <h6 className="text-uppercase text-muted fw-bold mb-4">System Alerts</h6>
              {results.alerts.length === 0 ? (
                <div className="text-center text-success py-4">
                  <CheckCircle2 size={32} className="mb-2" />
                  <div className="fw-bold">All costs within limits</div>
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {results.alerts.map((alert, idx) => (
                    <div key={idx} className={`alert alert-${alert.type === 'danger' ? 'danger' : 'warning'} mb-0 py-2 small`}>
                      <strong className="d-block text-uppercase">{alert.type}</strong>
                      {alert.msg}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>


      {/* Ledger Table */}
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-header  py-3">
          <h6 className="card-title mb-0 fw-bold text-dark">Detailed Ledger</h6>
        </div>
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light text-muted small text-uppercase">
              <tr>
                <th className="ps-4">Cost Category</th>
                <th className="text-end">Period Total</th>
                <th className="text-end pe-4">Cost Per {unit}</th>
              </tr>
            </thead>
            <tbody>
              {[
                { label: 'Depreciation (Asset)', method: results.breakdown.deprMethod, per: results.breakdown.depreciation, type: 'fixed' },
                { label: 'Financing Interest', method: 'Allocated', per: results.breakdown.finance, type: 'fixed' },
                { label: 'Insurance & Overhead', method: 'Allocated', per: results.breakdown.insurance, type: 'fixed' },
                { label: `${config.fuelSource.toUpperCase()} Consumption`, value: (safeFloat(results.breakdown.fuel) * results.periodMiles).toFixed(2), per: results.breakdown.fuel, type: 'var' },
                { label: 'Maintenance & Tires', value: (safeFloat(results.breakdown.maintenance) * results.periodMiles).toFixed(2), per: results.breakdown.maintenance, type: 'var' },
                { label: 'Labor & Benefits', value: (safeFloat(results.breakdown.labor) * results.periodMiles).toFixed(2), per: results.breakdown.labor, type: 'var' }
              ].map((row, idx) => (
                <tr key={idx} className={row.type === 'var' ? '' : ''}>
                  <td className="ps-4"><strong>
                    <span className={row.type === 'var' ? 'text-black fw-medium-bold' : 'text-dark'}>{row.label}</span>
                    {row.method && <div className="small text-muted">{row.method}</div>}
                    </strong>
                  </td>
                  <td className="text-end font-monospace text-muted">
                    {row.type === 'fixed' ? 'Allocated' : `${currency}${row.value}`}
                  </td>
                  <td className={`text-end pe-4 font-monospace ${row.type === 'var' ? 'text-black fw-bold' : ''}`}>
                    {row.per}
                  </td>
                </tr>
              ))}
              <tr className="table-secondary fw-bold">
                <td className="ps-4 py-3">GRAND TOTAL</td>
                <td className="text-end py-3">{currency}{results.totals.grand}</td>
                <td className="text-end pe-4 py-3">{currency}{results.cpm}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>


      {(config.region === 'CA' || config.region === 'AU') && (
        <div className="alert alert-primary d-flex justify-content-between align-items-center mb-0">
          <strong>Estimated Tax Deduction ({config.region})</strong>
          <span className="fs-5">{currency}{results.taxDeduction}</span>
        </div>
      )}
    </div>
  );
};


// --- MAIN WRAPPER ---


export default function TotalCostOfOwnership({ className = "", onComplete }) {
  const [activeStep, setActiveStep] = useState(1);
  const [reportPeriod, setReportPeriod] = useState({ start: '2025-01-01', end: '2025-01-31', days: 31 });
 
  const [config, setConfig] = useState({
    vehicleType: 'class8', region: 'US', estAnnualMiles: '120000',
    fuelSource: 'petrol', regNumber: '', makeModel: '', modelYear: new Date().getFullYear().toString()
  });
  const { sidebar, setSidebar, Dark, setDark } = useContext(AppContext);

  const [fixedData, setFixedData] = useState({
    purchasePrice: '', serviceLifeMiles: '', resaleValue: '', financeMethod: 'cash',
    interestRate: '7.5', monthlyPayment: '', insuranceAnnual: '', registrationAnnual: '',
    hutAnnual: '', permitsAnnual: '', adminOverheadAnnual: ''
  });


  const [varData, setVarData] = useState({
    fuelSpend: '', fuelQuantity: '', routineMaint: '', correctiveMaint: '',
    tires: '', washing: '', driverWages: '', benefits: '', tolls: '', fines: ''
  });


  const [telematics, setTelematics] = useState({ periodDistance: '', odometerEnd: '' });
  const [results, setResults] = useState(null);


  // Inject Bootstrap CSS on mount
  useEffect(() => {
    const link = document.createElement('link');
    link.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css";
    link.rel = "stylesheet";
    link.integrity = "sha384-9ndCyUaIbzAi2FUVXJi0CjmCapSmO7SnpJef0486qhLnuZ2cdeRhO02iuK6FUUVM";
    link.crossOrigin = "anonymous";
    document.head.appendChild(link);


    return () => {
      document.head.removeChild(link);
    };
  }, []);


  const handleCalculate = () => {
    const miles = safeFloat(telematics.periodDistance);
    if (miles <= 0) {
      alert("Please enter a valid total distance for the period (greater than 0) to calculate costs.");
      return;
    }


    const res = calculateTCO(config, fixedData, varData, telematics, reportPeriod.days);
    if (res) {
      setResults(res);
      setActiveStep(4);
      if (onComplete) onComplete(res);
    }
  };


  const loadDemoData = () => {
    setFixedData({ ...fixedData, purchasePrice: '165000', serviceLifeMiles: '600000', resaleValue: '45000', monthlyPayment: '2450', insuranceAnnual: '2200', registrationAnnual: '1800', hutAnnual: '550', permitsAnnual: '250', adminOverheadAnnual: '1200' });
    setVarData({ ...varData, fuelSpend: '5200', fuelQuantity: '1350', routineMaint: '450', correctiveMaint: '1200', tires: '200', washing: '80', driverWages: '11000', benefits: '2200', tolls: '350', fines: '0' });
    setTelematics({ periodDistance: '9850', odometerEnd: '125400' });
  };


  const steps = [
    {id: 1, label: 'Profile Setup', icon: Settings},
    {id: 2, label: 'Asset & Fixed', icon: DollarSign},
    {id: 3, label: 'Variable Ops', icon: TrendingUp},
    {id: 4, label: 'Analysis', icon: PieChart}
  ];


  return (
    <motion.div
              className={sidebar && sidebar ? "taskMain " : "cx-active taskMain"}
              id="cx-main"
              
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.1 }}
              key={"motionAnimation"}
            >
                <div
                  id="cx-wrapper"
                  className="dashboard_responsive"
                  key={"mainContainer"}
                >
                  <div className="main-dashboard-wrapper">
    <div >



      <div>
       
        {/* Stepper */}
        <div className="position-relative d-flex justify-content-between mb-5 px-4">
          <div className="position-absolute top-50 start-0 w-100 border-top border-secondary z-n1 opacity-25" style={{zIndex: 0}}></div>
          {steps.map((step) => (
            <button
              key={step.id}
              onClick={() => setActiveStep(step.id)}
              className="btn btn-link text-decoration-none p-0 d-flex flex-column align-items-center  z-1"
              style={{zIndex: 1}}
            >
              <div
                className={`rounded-circle d-flex align-items-center justify-content-center border border-2 transition-all ${activeStep === step.id ? ' border-black text-white' : 'bg-white border-secondary text-secondary'}`}
                style={{width: 45, height: 45,backgroundColor:"rgb(156, 73, 0)"}}
              >
                <step.icon size={20} />
              </div>
              <span className={`small mt-2 fw-bold ${activeStep === step.id ? 'text-black' : 'text-secondary'}`}>{step.label}</span>
            </button>
          ))}
        </div>


        {/* Content */}
        {activeStep === 1 && <ProfileStep config={config} setConfig={setConfig} onNext={() => setActiveStep(2)} />}
        {activeStep === 2 && <FixedCostsStep data={fixedData} setData={setFixedData} config={config} loadDemo={loadDemoData} onNext={() => setActiveStep(3)} onBack={() => setActiveStep(1)} />}
        {activeStep === 3 && <VariableCostsStep data={varData} setData={setVarData} telematics={telematics} setTelematics={setTelematics} config={config} reportPeriod={reportPeriod} onCalc={handleCalculate} onBack={() => setActiveStep(2)} />}
        {activeStep === 4 && <DashboardStep results={results} config={config} onEdit={() => setActiveStep(3)} />}
     
      </div>
    </div>
        </div>
    </div>
          </motion.div>
  );
}
